/**
 * Gamification configuration for the **DailyJournal** domain.
 *
 * The engine (`engine.ts`) is agnostic; here we declare WHAT this app rewards.
 * The journal's STRONG axis is the **writing streak**: writing for a day keeps
 * the streak (`writing`) and grants XP; badges celebrate consistency. Positive
 * and guilt-free (it never punishes unwritten days).
 *
 * `PlayerState` is recomputable on the client from already-synced entry days
 * (local-first): no new columns required. Pure → re-derivable on the server
 * (anti-fraud).
 */

import { counterAtLeast, levelAtLeast, streakAtLeast } from "./badges";
import { applyEvents, type GamificationConfig, initialState } from "./engine";
import type { GameEvent, PlayerState } from "./types";

/** Streak line: days with at least one written entry. */
export const WRITING_STREAK_LINE = "writing";

export const JOURNAL_EVENT = {
  written: "entry.written",
} as const;

export const XP_PER_ENTRY = 15;

export const JOURNAL_GAMIFICATION_CONFIG: GamificationConfig = {
  xp: {
    [JOURNAL_EVENT.written]: XP_PER_ENTRY,
  },
  streakLines: {
    [JOURNAL_EVENT.written]: [WRITING_STREAK_LINE],
  },
  counters: {
    [JOURNAL_EVENT.written]: ["entries"],
  },
  badges: [
    {
      id: "first-entry",
      title: "Primera página",
      when: counterAtLeast("entries", 1),
    },
    {
      id: "entries-10",
      title: "Diez entradas",
      when: counterAtLeast("entries", 10),
      bonusXp: 30,
    },
    {
      id: "entries-50",
      title: "Escritor constante",
      when: counterAtLeast("entries", 50),
      bonusXp: 75,
    },
    {
      id: "entries-100",
      title: "Cronista",
      when: counterAtLeast("entries", 100),
      bonusXp: 150,
    },
    {
      id: "writing-streak-3",
      title: "3 días escribiendo",
      when: streakAtLeast(WRITING_STREAK_LINE, 3),
      bonusXp: 15,
    },
    {
      id: "writing-streak-7",
      title: "Semana de escritura",
      when: streakAtLeast(WRITING_STREAK_LINE, 7),
      bonusXp: 40,
    },
    {
      id: "writing-streak-30",
      title: "Mes de escritura",
      when: streakAtLeast(WRITING_STREAK_LINE, 30),
      bonusXp: 120,
    },
    {
      id: "writing-streak-100",
      title: "Cien días de constancia",
      when: streakAtLeast(WRITING_STREAK_LINE, 100),
      bonusXp: 300,
    },
    { id: "level-5", title: "Nivel 5", when: levelAtLeast(5) },
    { id: "level-10", title: "Nivel 10", when: levelAtLeast(10) },
  ],
  quests: [
    {
      id: "weekly-writing",
      title: "Escribe 5 entradas esta semana",
      period: "weekly",
      goals: [{ metric: JOURNAL_EVENT.written, target: 5 }],
      bonusXp: 40,
    },
  ],
};

/** Day with entries (compatible with the aggregate row of `getEntryDaysQuery`). */
export interface JournalDayInput {
  /** Day `YYYY-MM-DD`. */
  date: string;
  /** Number of entries that day (≥1). */
  count: number;
}

/**
 * Derives the `GameEvent`s from the days with entries. Each entry scores
 * (XP + counter); the `writing` streak line fires once per day (the engine
 * deduplicates). Sorted by day for determinism.
 */
export function deriveJournalGameEvents(days: Iterable<JournalDayInput>): GameEvent[] {
  const events: GameEvent[] = [];
  const sorted = [...days]
    .filter((d) => d.date && d.date.length >= 10 && d.count > 0)
    .toSorted((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  for (const d of sorted) {
    const day = d.date.slice(0, 10);
    const at = Date.parse(`${day}T12:00:00.000Z`);
    for (let i = 0; i < d.count; i += 1) {
      events.push({ type: JOURNAL_EVENT.written, at, day });
    }
  }
  return events;
}

/** Recomposes the `PlayerState` from the days with entries (local-first, pure). */
export function computeJournalPlayerState(
  days: Iterable<JournalDayInput>,
  opts: { freezes?: number } = {},
): PlayerState {
  const events = deriveJournalGameEvents(days);
  return applyEvents(initialState(opts.freezes ?? 0), events, JOURNAL_GAMIFICATION_CONFIG).state;
}
